/**
 * The Slate realtime protocol.
 *
 * Both ends of the socket import this file, which is the point: the Worker
 * validates inbound frames with these schemas and the browser derives its
 * message types from the same declarations, so a protocol change that is not
 * made in both places fails to compile rather than failing at runtime in a
 * browser tab nobody is watching.
 *
 * ## Why Zod on the wire, and not just TypeScript types
 *
 * TypeScript types are erased. A socket is an unauthenticated input channel:
 * anything can arrive, including a 4 MB string, a `position` of `NaN` (which
 * survives `JSON.parse` as `null`, or arrives as a string), or an object with a
 * `__proto__` key. Casting `JSON.parse(raw) as ClientMessage` would make every
 * downstream handler trust that shape. Zod costs one parse per frame - a few
 * microseconds on the DO's single thread - and in exchange the rest of the
 * server can be written as if the input were typed, because it is.
 *
 * The union is discriminated on a short `t` tag rather than on a nested
 * `type`/`kind` field. Two reasons: it keeps frames small (cursor frames are
 * sent 20 times a second per client, so every byte of envelope is multiplied by
 * the fan-out), and `z.discriminatedUnion` rejects an unknown tag with a
 * precise error instead of trying every branch and reporting the union of all
 * their failures.
 *
 * ## Why the payload is nested under `d`
 *
 * Flattening the payload into the envelope would save eight bytes per frame and
 * make `t` collide with any payload field named `type` or `data` forever. The
 * nesting is cheap insurance and it makes `ServerMessage['d']` a usable type
 * that can be extracted per branch.
 *
 * ## Ordering guarantee
 *
 * Every frame carries the board `sequence` at which it was produced, except
 * presence frames, which are ephemeral and deliberately not sequenced. A client
 * that receives sequence 12 after sequence 14 knows it missed a frame and can
 * request a resync instead of silently rendering a board that disagrees with
 * everyone else's.
 */

import { z } from 'zod';
import { BOARD_EVENT_TYPES, PRIORITIES, type BoardEvent } from './domain.js';

// ---------------------------------------------------------------------------
// Tunables that both ends must agree on
// ---------------------------------------------------------------------------

/**
 * Cursor frames are coalesced to at most one per 50 ms per client (20 Hz).
 *
 * This is the single highest-leverage decision in the realtime layer. A pointer
 * moving across a board generates 60-120 events per second on a typical
 * trackpad and far more on a high-polling-rate mouse. Each one fans out to
 * every other participant, so a five-person board would emit 300-600 frames per
 * second of pure cursor traffic - more than every genuine board mutation
 * combined, and enough to make the board feel sluggish while nothing is
 * actually happening. 20 Hz is below the threshold where interpolation looks
 * smooth and above the threshold where motion looks steppy. The DO coalesces as
 * well as the client, because a malicious or buggy client must not be able to
 * set its own rate.
 */
export const CURSOR_THROTTLE_MS = 50;

/** Client sends `ping` on this interval; the DO replies `pong`. The interval is
 * short enough that a dead TCP connection is noticed within ~2 intervals, and
 * long enough that an idle board costs two frames a minute. Cloudflare's own
 * idle timeout for a hibernating socket is far longer, so this is a
 * client-side liveness probe, not a keepalive requirement. */
export const HEARTBEAT_INTERVAL_MS = 25_000;

/** A client that reconnects more than this many events behind is told to take a
 * full snapshot instead of a replay. Replaying 500 events costs the DO 500
 * reads and the client 500 reducer passes; a snapshot costs one indexed query
 * and one array replace. The crossover is somewhere around here, and the exact
 * number matters less than having a bound at all - without one, a client that
 * was offline overnight would force the DO to read its entire event history. */
export const MAX_REPLAY_EVENTS = 500;

/** Upper bound on presence entries the DO will report. A board with more than
 * 50 simultaneous viewers is not a board, it is a broadcast, and the avatar
 * stack stops being meaningful long before that. */
export const MAX_PRESENCE = 50;

// ---------------------------------------------------------------------------
// Shared fragments
// ---------------------------------------------------------------------------

export const cursorPositionSchema = z.object({
  // Clamped rather than rejected: a pointer captured slightly outside the board
  // surface during a fast drag is normal, and a hard rejection would drop the
  // final position of the gesture, leaving a peer's cursor stuck at the edge.
  x: z.number().min(0).max(1).catch(0),
  y: z.number().min(0).max(1).catch(0),
});

export const presenceSchema = z.object({
  clientId: z.string().min(1).max(64),
  userId: z.string().min(1).max(64),
  name: z.string().min(1).max(80),
  colour: z.string().min(1).max(32),
  cursor: cursorPositionSchema.nullable(),
  lastSeenAt: z.number().int().nonnegative(),
});

/**
 * The card patch accepted over the socket.
 *
 * `.strict()` is used here, unlike the HTTP schemas. The reason is that this
 * object is nested inside a discriminated union that has to be matched against
 * eleven other branches; letting unknown keys through would mean a typo like
 * `assignee` instead of `assigneeId` is silently discarded and the user's edit
 * appears to succeed while changing nothing. Over HTTP the same typo produces a
 * visible "no fields to update" 400. Here it is worth the stricter contract.
 */
export const cardPatchSchema = z
  .object({
    title: z.string().trim().min(1).max(280).optional(),
    description: z.string().trim().max(20_000).nullish(),
    assigneeId: z.string().uuid().nullish(),
    dueDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullish(),
    priority: z.enum(PRIORITIES).optional(),
    labelIds: z.array(z.string().uuid()).max(20).optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Client to server
// ---------------------------------------------------------------------------

/**
 * `hello` is sent twice in a socket's life: once immediately after `open`, and
 * once more after every automatic reconnect. On reconnect the client includes
 * `since`, the highest `sequence` it has successfully applied. That single
 * field is what turns a reconnect from "throw away the board and refetch it"
 * into "apply the four events you missed".
 *
 * `resumeClientId` is the `clientId` from the previous `welcome`. Supplying it
 * lets the DO keep the same presence identity across a reconnect, so the other
 * participants see the cursor blink rather than a leave/join pair. A client that
 * omits it is treated as a new participant, which is correct for a genuinely new
 * tab.
 */
export const helloMessageSchema = z.object({
  t: z.literal('hello'),
  d: z.object({
    since: z.number().int().min(0).nullable().default(null),
    resumeClientId: z.string().min(1).max(64).nullable().default(null),
    /** Last known cursor, so a reconnecting client does not have to wait for
     * the next pointer move before reappearing on peers' screens. */
    cursor: cursorPositionSchema.nullable().default(null),
  }),
});

export const cursorMessageSchema = z.object({
  t: z.literal('cursor'),
  d: cursorPositionSchema,
});

export const cardMoveMessageSchema = z.object({
  t: z.literal('card.move'),
  d: z.object({
    opId: z.string().min(8).max(64),
    cardId: z.string().uuid(),
    columnId: z.string().uuid(),
    /** Ordinal target index, not a fractional position. See the note on
     * `moveCardBodySchema` in http.ts - the reasoning is identical and the
     * server is the only party that computes fractional values. */
    position: z.number().int().min(0).max(100_000),
    expectedVersion: z.number().int().min(1),
  }),
});

export const cardCreateMessageSchema = z.object({
  t: z.literal('card.create'),
  d: z.object({
    opId: z.string().min(8).max(64),
    id: z.string().uuid().nullable().default(null),
    columnId: z.string().uuid(),
    title: z.string().trim().min(1).max(280),
    description: z.string().trim().max(20_000).nullish(),
    priority: z.enum(PRIORITIES).default('none'),
    assigneeId: z.string().uuid().nullish(),
    dueDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullish(),
  }),
});

export const cardUpdateMessageSchema = z.object({
  t: z.literal('card.update'),
  d: z.object({
    opId: z.string().min(8).max(64),
    cardId: z.string().uuid(),
    patch: cardPatchSchema,
    expectedVersion: z.number().int().min(1),
  }),
});

export const cardArchiveMessageSchema = z.object({
  t: z.literal('card.archive'),
  d: z.object({
    opId: z.string().min(8).max(64),
    cardId: z.string().uuid(),
    archived: z.boolean(),
    expectedVersion: z.number().int().min(1),
  }),
});

export const columnCreateMessageSchema = z.object({
  t: z.literal('column.create'),
  d: z.object({
    opId: z.string().min(8).max(64),
    name: z.string().trim().min(1).max(60),
  }),
});

export const columnReorderMessageSchema = z.object({
  t: z.literal('column.reorder'),
  d: z.object({
    opId: z.string().min(8).max(64),
    columnId: z.string().uuid(),
    position: z.number().int().min(0).max(10_000),
  }),
});

export const commentCreateMessageSchema = z.object({
  t: z.literal('comment.create'),
  d: z.object({
    opId: z.string().min(8).max(64),
    cardId: z.string().uuid(),
    body: z.string().trim().min(1).max(10_000),
  }),
});

/**
 * Typing indicators are ephemeral: they are never persisted, never sequenced
 * and never replayed. A user who reconnects while someone was mid-sentence
 * should not see a stale "typing..." badge that nothing will ever clear.
 */
export const typingMessageSchema = z.object({
  t: z.literal('typing'),
  d: z.object({
    cardId: z.string().uuid().nullable(),
    isTyping: z.boolean(),
  }),
});

export const pingMessageSchema = z.object({
  t: z.literal('ping'),
  d: z.object({
    /** Client clock, echoed back verbatim so the client can compute RTT
     * without trusting the server's clock, which it has no reason to share. */
    ts: z.number().int().nonnegative(),
  }),
});

export const clientMessageSchema = z.discriminatedUnion('t', [
  helloMessageSchema,
  cursorMessageSchema,
  cardMoveMessageSchema,
  cardCreateMessageSchema,
  cardUpdateMessageSchema,
  cardArchiveMessageSchema,
  columnCreateMessageSchema,
  columnReorderMessageSchema,
  commentCreateMessageSchema,
  typingMessageSchema,
  pingMessageSchema,
]);

export type ClientMessage = z.infer<typeof clientMessageSchema>;
export type ClientMessageType = ClientMessage['t'];

/** Narrow a `ClientMessage` to the branch carrying a given tag. Used by the DO
 * so that `handleCardMove` can take exactly the payload it needs rather than
 * the whole union and a cast. */
export type ClientMessageOf<T extends ClientMessageType> = Extract<ClientMessage, { t: T }>;

// ---------------------------------------------------------------------------
// Server to client
// ---------------------------------------------------------------------------

/**
 * Sent exactly once per socket, in response to `hello`.
 *
 * `replay` and `resyncRequired` are mutually exclusive by construction:
 *
 * - Fresh connection (`since: null`) - `replay` is `null`, `resyncRequired` is
 *   `false`, and the client already holds a snapshot from
 *   `GET /api/boards/:id/state`.
 * - Reconnect within the replay window - `replay` holds the missed events in
 *   ascending sequence order and the client applies them before processing any
 *   live frame.
 * - Reconnect beyond the window - `resyncRequired` is `true` and `replay` is
 *   `null`. The client must fetch a fresh snapshot. The DO does not send the
 *   snapshot itself because a snapshot can be hundreds of kilobytes and the DO
 *   is the wrong place to assemble it; the HTTP path can stream it and is
 *   cacheable.
 *
 * Sending the replay *inside* `welcome` rather than as a burst of separate
 * frames is what makes the handshake atomic: the client applies the replay and
 * sets `live = true` in the same task, so a live frame can never be interleaved
 * between two replayed ones.
 */
export const welcomePayloadSchema = z.object({
  clientId: z.string().min(1).max(64),
  boardId: z.string().min(1).max(64),
  sequence: z.number().int().min(0),
  serverTime: z.number().int().nonnegative(),
  presence: z.array(presenceSchema).max(MAX_PRESENCE),
  replay: z.array(z.custom<BoardEvent>()).nullable(),
  resyncRequired: z.boolean(),
});

export const presenceJoinMessageSchema = z.object({
  t: z.literal('presence.join'),
  d: z.object({ presence: presenceSchema }),
});

export const presenceLeaveMessageSchema = z.object({
  t: z.literal('presence.leave'),
  d: z.object({
    clientId: z.string().min(1).max(64),
    userId: z.string().min(1).max(64),
  }),
});

export const presenceCursorMessageSchema = z.object({
  t: z.literal('presence.cursor'),
  d: z.object({
    clientId: z.string().min(1).max(64),
    userId: z.string().min(1).max(64),
    cursor: cursorPositionSchema.nullable(),
  }),
});

/** Envelope shared by every persisted mutation broadcast. `sequence` is the
 * board sequence the event was assigned, and `opId` is present only on the
 * socket that originated the mutation - it is the acknowledgement that retires
 * that client's optimistic entry, and it is not sent to anyone else because it
 * would be meaningless to them. */
const sequenced = { sequence: z.number().int().min(1), opId: z.string().min(8).max(64).optional() };

export const cardCreatedMessageSchema = z.object({
  t: z.literal('card.created'),
  d: z.object({ ...sequenced, card: z.custom<unknown>() }),
});

export const cardMovedMessageSchema = z.object({
  t: z.literal('card.moved'),
  d: z.object({ ...sequenced, card: z.custom<unknown>() }),
});

export const cardUpdatedMessageSchema = z.object({
  t: z.literal('card.updated'),
  d: z.object({ ...sequenced, card: z.custom<unknown>() }),
});

export const cardArchivedMessageSchema = z.object({
  t: z.literal('card.archived'),
  d: z.object({ ...sequenced, card: z.custom<unknown>() }),
});

export const columnCreatedMessageSchema = z.object({
  t: z.literal('column.created'),
  d: z.object({ ...sequenced, column: z.custom<unknown>() }),
});

/**
 * A reorder can renumber an entire column, so it ships the positions rather
 * than a single column. Sending `{ columnId, position }` would be wrong when
 * the reorder triggered a rebalance: the client would apply one position and
 * keep the other n-1 stale, and the board would render in a different order for
 * that user than for everyone else until the next full refresh.
 */
export const columnReorderedMessageSchema = z.object({
  t: z.literal('column.reordered'),
  d: z.object({
    ...sequenced,
    positions: z.array(z.object({ id: z.string(), position: z.number() })).max(200),
  }),
});

export const commentCreatedMessageSchema = z.object({
  t: z.literal('comment.created'),
  d: z.object({ ...sequenced, comment: z.custom<unknown>() }),
});

export const typingBroadcastSchema = z.object({
  t: z.literal('typing'),
  d: z.object({
    userId: z.string(),
    name: z.string(),
    cardId: z.string().nullable(),
    isTyping: z.boolean(),
  }),
});

/**
 * Errors are scoped to a single frame, never to the connection. A rejected
 * `card.move` (stale version) must not tear down the socket - the client needs
 * the connection to receive the authoritative state that resolves the conflict.
 * Only a protocol violation or an authentication failure closes the socket, and
 * those use `fatal: true` so the client knows not to reconnect.
 */
export const errorMessageSchema = z.object({
  t: z.literal('error'),
  d: z.object({
    code: z.string().min(1).max(64),
    message: z.string().min(1).max(500),
    /** The optimistic mutation this error refers to, when the failure is
     * attributable to one. Absent for errors with no originating op. */
    opId: z.string().min(8).max(64).optional(),
    /** Present on `version_conflict` so the client can reconcile immediately
     * without a round trip. This is the payload that makes rollback feel
     * instant rather than like a failed request. */
    current: z.custom<unknown>().optional(),
    fatal: z.boolean().optional(),
  }),
});

export const pongMessageSchema = z.object({
  t: z.literal('pong'),
  d: z.object({
    ts: z.number().int().nonnegative(),
    serverTime: z.number().int().nonnegative(),
  }),
});

/**
 * The catch-all channel for persisted events that have no dedicated message.
 *
 * Every `board_events` row can be expressed as a `ServerMessage`. The
 * high-traffic ones (`card.*`, `column.*`, `comment.created`) get dedicated
 * branches because they carry a hydrated entity the client can drop straight
 * into state. The rest - `board.updated`, `card.deleted`, `comment.deleted`,
 * `attachment.created`, `member.joined` - travel as `event` with the raw
 * `BoardEvent`, which the client's generic reducer handles.
 *
 * Having a catch-all is what makes replay safe: adding a new event type on the
 * server cannot produce a frame the client is unable to parse, so a
 * server-first deploy degrades to "the activity feed shows it, the board does
 * not update until refresh" instead of "the socket crashes on an unknown tag".
 */
export const eventMessageSchema = z.object({
  t: z.literal('event'),
  d: z.object({ event: z.custom<BoardEvent>() }),
});

export const serverMessageSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('welcome'), d: welcomePayloadSchema }),
  presenceJoinMessageSchema,
  presenceLeaveMessageSchema,
  presenceCursorMessageSchema,
  cardCreatedMessageSchema,
  cardMovedMessageSchema,
  cardUpdatedMessageSchema,
  cardArchivedMessageSchema,
  columnCreatedMessageSchema,
  columnReorderedMessageSchema,
  commentCreatedMessageSchema,
  typingBroadcastSchema,
  errorMessageSchema,
  pongMessageSchema,
  eventMessageSchema,
]);

export type ServerMessage = z.infer<typeof serverMessageSchema>;
export type ServerMessageType = ServerMessage['t'];
export type ServerMessageOf<T extends ServerMessageType> = Extract<ServerMessage, { t: T }>;
export type WelcomePayload = z.infer<typeof welcomePayloadSchema>;

// ---------------------------------------------------------------------------
// WebSocket close codes
// ---------------------------------------------------------------------------

/**
 * Application close codes live in the 4000-4999 range reserved by RFC 6455.
 * The client's reconnect logic reads these: 4001 and 4003 are terminal (the
 * user must sign in again or the board is gone), everything else is retryable.
 * Encoding that distinction in the close code is what stops the client from
 * reconnecting forever against a board it will never be allowed to join.
 */
export const CLOSE_CODES = {
  /** Normal closure - the tab navigated away. Never retried. */
  NORMAL: 1000,
  /** Token expired or invalid. Terminal: sign in again. */
  UNAUTHORIZED: 4001,
  /** The board does not exist, or the user is not a member of its workspace. */
  FORBIDDEN: 4003,
  /** The DO is shutting down or restarting. Retryable with backoff. */
  GOING_AWAY: 4004,
  /** A frame failed schema validation repeatedly. Terminal: the client is
   * speaking a protocol this server does not understand, so reconnecting would
   * loop. */
  PROTOCOL_ERROR: 4008,
  /** Too many frames per second. Retryable, but the client should back off
   * harder than usual. */
  RATE_LIMITED: 4029,
} as const;

export type CloseCode = (typeof CLOSE_CODES)[keyof typeof CLOSE_CODES];

/** Close codes that must not be retried. Kept as a set so the client's
 * reconnect predicate is a lookup rather than a growing switch. */
export const TERMINAL_CLOSE_CODES: ReadonlySet<number> = new Set([
  CLOSE_CODES.NORMAL,
  CLOSE_CODES.UNAUTHORIZED,
  CLOSE_CODES.FORBIDDEN,
  CLOSE_CODES.PROTOCOL_ERROR,
]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parse an inbound frame. Returns a discriminated result rather than throwing,
 * because a malformed frame is an expected event on a public socket, not an
 * exceptional one - and because the DO wants to count parse failures per socket
 * in order to decide when to close with `PROTOCOL_ERROR`.
 *
 * Size is checked before parsing. `JSON.parse` on a 10 MB frame is a
 * denial-of-service primitive on a single-threaded Durable Object, and the
 * check costs one comparison.
 */
export const MAX_FRAME_BYTES = 64 * 1024;

export type ParseResult =
  | { ok: true; message: ClientMessage }
  | { ok: false; code: 'too_large' | 'invalid_json' | 'invalid_shape'; detail: string };

export function parseClientMessage(raw: string | ArrayBuffer): ParseResult {
  if (typeof raw !== 'string') {
    return { ok: false, code: 'invalid_json', detail: 'binary frames are not accepted' };
  }
  if (raw.length > MAX_FRAME_BYTES) {
    return { ok: false, code: 'too_large', detail: `frame exceeds ${MAX_FRAME_BYTES} bytes` };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, code: 'invalid_json', detail: 'frame is not valid JSON' };
  }

  const result = clientMessageSchema.safeParse(parsed);
  if (!result.success) {
    // The first issue only. Echoing every issue back would let a client
    // enumerate the schema and would make the error frame larger than the
    // frame that caused it.
    const issue = result.error.issues[0];
    const path = issue?.path.join('.') ?? 'frame';
    return { ok: false, code: 'invalid_shape', detail: `${path}: ${issue?.message ?? 'invalid'}` };
  }

  return { ok: true, message: result.data };
}

export function encodeServerMessage(message: ServerMessage): string {
  return JSON.stringify(message);
}

/** Every event type the server can persist, re-exported so the client and the
 * DO's exhaustiveness checks share one list. */
export const EVENT_TYPES = BOARD_EVENT_TYPES;
